import React from "react";
import PropTypes from "prop-types";
import { Input, Icon } from "antd";
import { getAnalysisFieldDisplayValue } from "utilities/bank-statement-analyser-utils";

const ANALYSIS_VALUE_STYLE = {
    marginBottom: "0",
    fontSize: "18px",
    lineHeight: "1.4",
    color: "rgb(86 84 84 / 85%)",
    // "Full value" numbers (e.g. "31,70,354.00") are much wider than the
    // compact L/Cr form — without this a flex child refuses to shrink below
    // its content width and overflows into the next grid column instead of
    // wrapping.
    minWidth: 0,
    overflowWrap: "break-word",
    wordBreak: "break-word",
};

// Sub-row (the 6-month figure stacked under its base metric) reads smaller —
// it's the secondary number, the base value above it is the headline.
const ANALYSIS_VALUE_STYLE_SMALL = {
    ...ANALYSIS_VALUE_STYLE,
    fontSize: "14px",
};

class EditableAnalysisField extends React.Component {
    state = {
        isEditing: false,
        draftValue: "",
    };

    startEdit = () => {
        const { rawValue } = this.props;
        this.setState({
            isEditing: true,
            draftValue: rawValue != null && rawValue !== "" ? String(rawValue) : "",
        });
    };

    commitEdit = () => {
        if (!this.state.isEditing) {
            return;
        }
        const { draftValue } = this.state;
        this.setState({ isEditing: false, draftValue: "" });
        this.props.onSave(draftValue);
    };

    render() {
        const {
            fieldKey,
            rawValue,
            isLoading,
            editable,
            compact,
            lakhsOnly,
            small,
        } = this.props;
        const displayValue = getAnalysisFieldDisplayValue(
            fieldKey,
            { [fieldKey]: rawValue },
            isLoading,
            compact,
            lakhsOnly
        );
        const valueStyle = small ? ANALYSIS_VALUE_STYLE_SMALL : ANALYSIS_VALUE_STYLE;

        if (this.state.isEditing) {
            return (
                <Input
                    autoFocus
                    size="small"
                    value={this.state.draftValue}
                    onChange={(e) => this.setState({ draftValue: e.target.value })}
                    onBlur={this.commitEdit}
                    onPressEnter={this.commitEdit}
                    style={{
                        ...valueStyle,
                        width: "96px",
                        height: "32px",
                    }}
                />
            );
        }

        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: small ? "24px" : "32px",
                    minWidth: 0,
                    width: "100%",
                }}
            >
                <span style={valueStyle}>{displayValue}</span>
                {editable ? (
                    <Icon
                        type="edit"
                        style={{ marginLeft: "8px", cursor: "pointer", color: "#1890ff" }}
                        onClick={this.startEdit}
                    />
                ) : null}
            </div>
        );
    }
}

EditableAnalysisField.propTypes = {
    fieldKey: PropTypes.string.isRequired,
    rawValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isLoading: PropTypes.bool,
    editable: PropTypes.bool,
    compact: PropTypes.bool,
    lakhsOnly: PropTypes.bool,
    // Renders at the smaller sub-row scale (the 6-month figure stacked under
    // its base metric).
    small: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
};

EditableAnalysisField.defaultProps = {
    isLoading: false,
    editable: false,
    compact: false,
    lakhsOnly: false,
    small: false,
};

export default EditableAnalysisField;
